/* ============================================
   RN MAP FILTERS — standalone filter bar controller
   rn-map-filters.js
   ============================================
   Owns the filter state. Dispatches rn:map-filter
   when state changes. Listens to nothing — this is
   the source of truth. Map and cards sections
   subscribe to rn:map-filter only.

   Taxonomy locked to GeoJSON event_type values:
     RUNS:   run & event - multi day, run - multi day,
             run - one day, run & event - one day
     EVENTS: ford event, race, expo, event - multi day,
             car show
     SOCIAL: caravan, charity, meetup

   CLUB is surfaced via the separate "Clubs Only" toggle,
   not via the type dropdown.
   ============================================ */

var RNMapFilters = (function() {
  'use strict';

  var config = {};
  var btnObserver = null;

  /* ══════════════════════════════════════════
     FILTER STATE
     ══════════════════════════════════════════ */
  var state = {
    eventsOn:    true,
    types:       [],
    region:      'all',
    showClubs:   false,
    vendorsOn:   false,
    vendorTypes: [],
    hidePast:    true,
    month:       null,
    year:        null
  };

  var MONTH_NAMES = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  /* Type catalog — single source of truth for labels + grouping.
     Used by both desktop dropdown rendering (already in Liquid)
     and mobile sheet (rendered by JS). */
  var TYPE_CATALOG = [
    { group: 'Runs',   val: 'run & event - multi day', label: 'Run & Event', sub: ' - Multi Day', class: 'run' },
    { group: 'Runs',   val: 'run - multi day',         label: 'Run',         sub: ' - Multi Day', class: 'run' },
    { group: 'Runs',   val: 'run - one day',           label: 'Run',         sub: ' - One Day',   class: 'run' },
    { group: 'Runs',   val: 'run & event - one day',   label: 'Run & Event', sub: ' - One Day',   class: 'run' },
    { group: 'Events', val: 'ford event',              label: 'Ford Event',  sub: '',             class: 'ford' },
    { group: 'Events', val: 'race',                    label: 'Race',        sub: '',             class: 'event' },
    { group: 'Events', val: 'expo',                    label: 'Expo',        sub: '',             class: 'event' },
    { group: 'Events', val: 'event - multi day',       label: 'Event',       sub: ' - Multi Day', class: 'event' },
    { group: 'Events', val: 'car show',                label: 'Car Show',    sub: '',             class: 'event' },
    { group: 'Social', val: 'caravan',                 label: 'Caravan',     sub: '',             class: 'social' },
    { group: 'Social', val: 'charity',                 label: 'Charity',     sub: '',             class: 'social' },
    { group: 'Social', val: 'meetup',                  label: 'Meetup',      sub: '',             class: 'social' }
  ];

  var COLOR_BY_CLASS = {
    'run':    '#3ddc6e',
    'ford':   '#4a9eff',
    'event':  '#ff3d8a',
    'social': '#ff851b'
  };

  function normalizeType(s) { return s ? String(s).trim().toLowerCase() : ''; }

  function escapeHTML(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  function formatType(s) {
    if (!s) return '';
    var found = null;
    var n = normalizeType(s);
    for (var i = 0; i < TYPE_CATALOG.length; i++) {
      if (TYPE_CATALOG[i].val === n) { found = TYPE_CATALOG[i]; break; }
    }
    if (found) return found.label + (found.sub || '');
    return s.trim().split(' ').map(function(w) {
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    }).join(' ');
  }

  /* ══════════════════════════════════════════
     INIT
     ══════════════════════════════════════════ */
  function init(opts) {
    config = opts;

    bindDropdowns();
    bindMultiSelectType();
    bindRegionSelect();
    bindToggles();
    bindVendorTypeSelect();
    bindDateControls();
    bindMobileSheet();

    /* Dispatch initial state once so map and cards apply default filters
       (e.g. hide past events). */
    dispatch();
  }

  /* ══════════════════════════════════════════
     DROPDOWN OPEN/CLOSE
     ══════════════════════════════════════════ */
  function bindDropdowns() {
    var sid = config.sectionId;

    document.addEventListener('click', function(e) {
      if (!e.target.closest('.rn-mf-dd')) closeAllDropdowns();
    });

    /* Map of dropdown key → element id slug (must match the IDs in liquid) */
    var DROPDOWNS = {
      'type':           'rn-mf-dd-type-' + sid,
      'region':         'rn-mf-dd-region-' + sid,
      'date':           'rn-mf-dd-date-' + sid,
      'vendors-types':  'rn-mf-dd-vendors-' + sid,
      'cta':            'rn-mf-dd-cta-' + sid
    };

    Object.keys(DROPDOWNS).forEach(function(key) {
      var wrap = document.getElementById(DROPDOWNS[key]);
      if (!wrap) return;
      var trigger  = wrap.querySelector('[data-dd="' + key + '"]');
      var dropdown = wrap.querySelector('.rn-mf-dropdown');
      if (!trigger || !dropdown) return;

      trigger.addEventListener('click', function(e) {
        e.stopPropagation();
        var open = dropdown.classList.contains('is-open');
        closeAllDropdowns();
        if (!open) {
          dropdown.classList.add('is-open');
          trigger.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });

    /* Apply / close buttons inside type & vendors dropdowns */
    var typeDD = document.getElementById('rn-mf-dd-type-' + sid);
    if (typeDD) {
      var apply = typeDD.querySelector('[data-close="type"]');
      if (apply) apply.addEventListener('click', function() { closeAllDropdowns(); });
    }
    var vendorsDD = document.getElementById('rn-mf-dd-vendors-' + sid);
    if (vendorsDD) {
      var vApply = vendorsDD.querySelector('[data-close="vendors-types"]');
      if (vApply) vApply.addEventListener('click', function() { closeAllDropdowns(); });
    }
  }

  function closeAllDropdowns() {
    var sid = config.sectionId;
    var ALL = ['type', 'region', 'date', 'vendors-types', 'cta'];
    var ID_MAP = {
      'type':          'rn-mf-dd-type-' + sid,
      'region':        'rn-mf-dd-region-' + sid,
      'date':          'rn-mf-dd-date-' + sid,
      'vendors-types': 'rn-mf-dd-vendors-' + sid,
      'cta':           'rn-mf-dd-cta-' + sid
    };
    ALL.forEach(function(key) {
      var wrap = document.getElementById(ID_MAP[key]);
      if (!wrap) return;
      var trigger  = wrap.querySelector('[data-dd="' + key + '"]');
      var dropdown = wrap.querySelector('.rn-mf-dropdown');
      if (trigger)  { trigger.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); }
      if (dropdown) { dropdown.classList.remove('is-open'); }
    });
  }

  /* ══════════════════════════════════════════
     EVENT TYPE MULTI-SELECT
     ══════════════════════════════════════════ */
  function bindMultiSelectType() {
    var sid = config.sectionId;
    var wrap = document.getElementById('rn-mf-dd-type-' + sid);
    if (!wrap) return;

    wrap.querySelectorAll('.rn-mf-opt').forEach(function(opt) {
      opt.addEventListener('click', function(e) {
        e.stopPropagation();
        var v = opt.getAttribute('data-val');
        if (!v) return;
        var idx = state.types.indexOf(v);
        if (idx === -1) state.types.push(v);
        else            state.types.splice(idx, 1);
        opt.classList.toggle('is-sel', idx === -1);
        opt.setAttribute('aria-selected', idx === -1 ? 'true' : 'false');

        /* Selecting a type implicitly turns the events layer on. */
        if (state.types.length > 0 && !state.eventsOn) {
          state.eventsOn = true;
          var eBtn = document.querySelector('#rn-map-filters-' + sid + ' [data-toggle="events"]');
          if (eBtn) {
            eBtn.classList.add('is-on');
            eBtn.setAttribute('aria-pressed', 'true');
          }
        }
        updateTypeTrigger();
        dispatch();
      });
    });

    var clearBtn = wrap.querySelector('[data-clear="type"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        state.types = [];
        wrap.querySelectorAll('.rn-mf-opt').forEach(function(o) {
          o.classList.remove('is-sel');
          o.setAttribute('aria-selected', 'false');
        });
        updateTypeTrigger();
        dispatch();
      });
    }
  }

  function updateTypeTrigger() {
    var wrap = document.getElementById('rn-mf-dd-type-' + config.sectionId);
    if (!wrap) return;
    var trigger = wrap.querySelector('[data-dd="type"]');
    var textEl  = trigger ? trigger.querySelector('.rn-mf-trigger__text') : null;
    var badge   = trigger ? trigger.querySelector('.rn-mf-trigger__badge') : null;
    var n = state.types.length;

    if (textEl)  textEl.textContent = n === 0 ? 'All Events' : n === 1 ? formatType(state.types[0]) : 'Event Types';
    if (badge)   {
      badge.textContent = n > 1 ? String(n) : '';
      badge.classList.toggle('is-on', n > 1);
    }
    if (trigger) trigger.classList.toggle('has-val', n > 0);
  }

  /* ══════════════════════════════════════════
     REGION SINGLE-SELECT
     ══════════════════════════════════════════ */
  function bindRegionSelect() {
    var sid = config.sectionId;
    var wrap = document.getElementById('rn-mf-dd-region-' + sid);
    if (!wrap) return;

    wrap.querySelectorAll('.rn-mf-region-opt').forEach(function(opt) {
      opt.addEventListener('click', function(e) {
        e.stopPropagation();
        var v = opt.getAttribute('data-val');
        if (!v) return;
        state.region = v;
        wrap.querySelectorAll('.rn-mf-region-opt').forEach(function(o) {
          var sel = o === opt;
          o.classList.toggle('is-selected', sel);
          o.setAttribute('aria-selected', sel ? 'true' : 'false');
        });
        updateRegionTrigger();
        closeAllDropdowns();
        dispatch();
      });
    });
  }

  function updateRegionTrigger() {
    var wrap = document.getElementById('rn-mf-dd-region-' + config.sectionId);
    if (!wrap) return;
    var trigger = wrap.querySelector('[data-dd="region"]');
    var textEl  = trigger ? trigger.querySelector('.rn-mf-trigger__text') : null;
    var labels = {
      'all':'Region','northeast':'Northeast','southeast':'Southeast','midwest':'Midwest',
      'southwest':'Southwest','west':'West','mexico':'Mexico','canada':'Canada'
    };
    if (textEl)  textEl.textContent = labels[state.region] || 'Region';
    if (trigger) trigger.classList.toggle('has-val', state.region !== 'all');
  }

  /* ══════════════════════════════════════════
     CLUBS + VENDORS TOGGLES
     ══════════════════════════════════════════ */
  function bindToggles() {
    var sid = config.sectionId;

    /* EVENTS toggle — defaults ON. OFF clears type sub-selections. */
    var eventsBtn = document.querySelector('#rn-map-filters-' + sid + ' [data-toggle="events"]');
    if (eventsBtn) {
      eventsBtn.addEventListener('click', function() {
        state.eventsOn = !state.eventsOn;
        eventsBtn.classList.toggle('is-on', state.eventsOn);
        eventsBtn.setAttribute('aria-pressed', String(state.eventsOn));
        if (!state.eventsOn) {
          state.types = [];
          /* Clear the visual selection inside the events dropdown */
          var typeDD = document.getElementById('rn-mf-dd-type-' + sid);
          if (typeDD) {
            typeDD.querySelectorAll('.rn-mf-opt').forEach(function(opt) {
              opt.classList.remove('is-sel');
              opt.setAttribute('aria-selected', 'false');
            });
          }
          updateTypeTrigger();
        }
        dispatch();
      });
    }

    /* VENDORS toggle — defaults OFF. OFF clears vendor type sub-selections. */
    var vendorsBtn = document.querySelector('#rn-map-filters-' + sid + ' [data-toggle="vendors"]');
    if (vendorsBtn) {
      vendorsBtn.addEventListener('click', function() {
        state.vendorsOn = !state.vendorsOn;
        vendorsBtn.classList.toggle('is-on', state.vendorsOn);
        vendorsBtn.setAttribute('aria-pressed', String(state.vendorsOn));
        if (!state.vendorsOn) {
          state.vendorTypes = [];
          var vendorsDD = document.getElementById('rn-mf-dd-vendors-' + sid);
          if (vendorsDD) {
            vendorsDD.querySelectorAll('.rn-mf-opt').forEach(function(opt) {
              opt.classList.remove('is-sel');
              opt.setAttribute('aria-selected', 'false');
            });
          }
          updateVendorsTriggerLabel();
        }
        dispatch();
      });
    }

    /* CLUBS toggle — defaults OFF. No dropdown / sub-selections. */
    var clubsBtn = document.querySelector('#rn-map-filters-' + sid + ' [data-toggle="clubs"]');
    if (clubsBtn) {
      clubsBtn.addEventListener('click', function() {
        state.showClubs = !state.showClubs;
        clubsBtn.classList.toggle('is-on', state.showClubs);
        clubsBtn.setAttribute('aria-pressed', String(state.showClubs));
        dispatch();
      });
    }
  }

  /* Vendor sub-types now live inside a multi-select dropdown.
     This handler mirrors bindMultiSelectType but writes to state.vendorTypes. */
  function bindVendorTypeSelect() {
    var dd = document.getElementById('rn-mf-dd-vendors-' + config.sectionId);
    if (!dd) return;

    dd.querySelectorAll('.rn-mf-opt[data-vtype]').forEach(function(opt) {
      opt.addEventListener('click', function() {
        var v = opt.getAttribute('data-vtype');
        var idx = state.vendorTypes.indexOf(v);
        if (idx === -1) {
          state.vendorTypes.push(v);
          opt.classList.add('is-sel');
          opt.setAttribute('aria-selected', 'true');
        } else {
          state.vendorTypes.splice(idx, 1);
          opt.classList.remove('is-sel');
          opt.setAttribute('aria-selected', 'false');
        }
        /* Selecting a vendor type implicitly turns the vendors layer on. */
        if (state.vendorTypes.length > 0 && !state.vendorsOn) {
          state.vendorsOn = true;
          var vBtn = document.querySelector('#rn-map-filters-' + config.sectionId + ' [data-toggle="vendors"]');
          if (vBtn) {
            vBtn.classList.add('is-on');
            vBtn.setAttribute('aria-pressed', 'true');
          }
        }
        updateVendorsTriggerLabel();
        dispatch();
      });
    });

    /* Clear button */
    var clearBtn = dd.querySelector('[data-clear="vendors-types"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function() {
        state.vendorTypes = [];
        dd.querySelectorAll('.rn-mf-opt').forEach(function(opt) {
          opt.classList.remove('is-sel');
          opt.setAttribute('aria-selected', 'false');
        });
        updateVendorsTriggerLabel();
        dispatch();
      });
    }

    /* Apply button — close dropdown */
    var applyBtn = dd.querySelector('[data-close="vendors-types"]');
    if (applyBtn) {
      applyBtn.addEventListener('click', function() {
        dd.classList.remove('is-open');
        var trigger = dd.querySelector('[data-dd="vendors-types"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }
  }

  function updateVendorsTriggerLabel() {
    var dd = document.getElementById('rn-mf-dd-vendors-' + config.sectionId);
    if (!dd) return;
    var trigger = dd.querySelector('[data-dd="vendors-types"]');
    if (!trigger) return;
    var badgeEl = trigger.querySelector('.rn-mf-trigger__badge');
    var n = state.vendorTypes.length;
    if (badgeEl) {
      if (n > 0) {
        badgeEl.textContent = n;
        badgeEl.classList.add('is-on');
      } else {
        badgeEl.textContent = '';
        badgeEl.classList.remove('is-on');
      }
    }
    trigger.classList.toggle('has-val', n > 0);
  }

  /* ══════════════════════════════════════════
     DATE
     ══════════════════════════════════════════ */
  function bindDateControls() {
    var pastTrack = document.getElementById(config.pastTrackId);
    if (pastTrack) {
      pastTrack.addEventListener('click', function() {
        state.hidePast = !state.hidePast;
        pastTrack.classList.toggle('is-on', state.hidePast);
        pastTrack.setAttribute('aria-checked', String(state.hidePast));
        updateDateTrigger();
        dispatch();
      });
    }

    var monthSel = document.getElementById(config.monthSelId);
    var yearSel  = document.getElementById(config.yearSelId);

    if (monthSel) {
      monthSel.addEventListener('change', function() {
        state.month = monthSel.value ? parseInt(monthSel.value, 10) : null;
        updateDateTrigger();
        dispatch();
      });
    }
    if (yearSel) {
      yearSel.addEventListener('change', function() {
        state.year = yearSel.value ? parseInt(yearSel.value, 10) : null;
        updateDateTrigger();
        dispatch();
      });
    }
  }

  function updateDateTrigger() {
    var wrap = document.getElementById('rn-mf-dd-date-' + config.sectionId);
    if (!wrap) return;
    var trigger = wrap.querySelector('[data-dd="date"]');
    var textEl  = trigger ? trigger.querySelector('.rn-mf-trigger__text') : null;
    var hasMY = state.month !== null && state.year !== null;
    var label = 'Date', hasVal = false;

    if (hasMY)               { label = MONTH_NAMES[state.month] + ' ' + state.year; hasVal = true; }
    else if (state.hidePast) { label = 'Upcoming'; hasVal = true; }

    if (textEl)  textEl.textContent = label;
    if (trigger) trigger.classList.toggle('has-val', hasVal);
  }

  /* ══════════════════════════════════════════
     ACTIVE FILTER STRIP
     ══════════════════════════════════════════ */
  function updateActiveStrip() {
    var container = document.getElementById(config.activeFiltersId);
    if (!container) return;

    var pills = [];

    if (state.types.length > 0) {
      pills.push({ key:'types', label:'Type',
        val: state.types.length === 1 ? formatType(state.types[0]) : state.types.length + ' selected' });
    }
    if (state.region !== 'all') {
      var labels = { 'northeast':'Northeast','southeast':'Southeast','midwest':'Midwest','southwest':'Southwest','west':'West','mexico':'Mexico','canada':'Canada' };
      pills.push({ key:'region', label:'Region', val: labels[state.region] || state.region });
    }
    if (state.showClubs) pills.push({ key:'clubs', label:'Clubs', val:'Showing' });
    if (state.vendorsOn) pills.push({ key:'vendors', label:'Vendors', val: state.vendorTypes.length > 0 ? state.vendorTypes.join(', ') : 'All' });
    if (state.month !== null && state.year !== null) {
      pills.push({ key:'date', label:'Date', val: MONTH_NAMES[state.month] + ' ' + state.year });
    } else if (state.hidePast) {
      pills.push({ key:'date', label:'Date', val:'Upcoming' });
    }

    container.innerHTML = '';
    pills.forEach(function(p) {
      var pill = document.createElement('div');
      pill.className = 'rn-mf-active-pill';
      pill.innerHTML =
        escapeHTML(p.label) +
        ': <span class="rn-mf-active-pill__val">' + escapeHTML(p.val) + '</span>' +
        '<button class="rn-mf-active-pill__x" type="button" aria-label="Remove ' + escapeHTML(p.label) + ' filter">×</button>';
      pill.querySelector('.rn-mf-active-pill__x').addEventListener('click', function() { clearFilter(p.key); });
      container.appendChild(pill);
    });
  }

  function clearFilter(key) {
    var sid = config.sectionId;

    if (key === 'types') {
      state.types = [];
      var wrap = document.getElementById('rn-mf-dd-type-' + sid);
      if (wrap) wrap.querySelectorAll('.rn-mf-opt').forEach(function(o) {
        o.classList.remove('is-sel');
        o.setAttribute('aria-selected', 'false');
      });
      updateTypeTrigger();
    }
    if (key === 'region') {
      state.region = 'all';
      var rwrap = document.getElementById('rn-mf-dd-region-' + sid);
      if (rwrap) rwrap.querySelectorAll('.rn-mf-region-opt').forEach(function(o) {
        var isAll = o.getAttribute('data-val') === 'all';
        o.classList.toggle('is-selected', isAll);
        o.setAttribute('aria-selected', isAll ? 'true' : 'false');
      });
      updateRegionTrigger();
    }
    if (key === 'clubs') {
      state.showClubs = false;
      var clubs = document.querySelector('#rn-map-filters-' + sid + ' [data-toggle="clubs"]');
      if (clubs) { clubs.classList.remove('is-on'); clubs.setAttribute('aria-pressed', 'false'); }
    }
    if (key === 'vendors') {
      state.vendorsOn = false; state.vendorTypes = [];
      var vBtn = document.querySelector('#rn-map-filters-' + sid + ' [data-toggle="vendors"]');
      var vRev = document.getElementById(config.vendorRevealId);
      if (vBtn) { vBtn.classList.remove('is-on'); vBtn.setAttribute('aria-pressed', 'false'); }
      if (vRev) {
        vRev.classList.remove('is-open');
        vRev.setAttribute('aria-hidden', 'true');
        vRev.querySelectorAll('.rn-mf-vtype').forEach(function(b) { b.classList.remove('is-on'); });
      }
    }
    if (key === 'date') {
      state.hidePast = false; state.month = null; state.year = null;
      var pt = document.getElementById(config.pastTrackId);
      var ms = document.getElementById(config.monthSelId);
      var ys = document.getElementById(config.yearSelId);
      if (pt) { pt.classList.remove('is-on'); pt.setAttribute('aria-checked', 'false'); }
      if (ms) ms.value = '';
      if (ys) ys.value = '';
      updateDateTrigger();
    }
    dispatch();
  }

  /* ══════════════════════════════════════════
     MOBILE BOTTOM SHEET
     ══════════════════════════════════════════ */
  function buildSheetHTML() {
    var html = '';
    var groups = ['Runs', 'Events', 'Social'];

    groups.forEach(function(group) {
      html += '<div class="rn-mf-sheet-group-label">' + group + '</div>';
      TYPE_CATALOG.filter(function(t) { return t.group === group; }).forEach(function(t) {
        var sel = state.types.indexOf(t.val) > -1 ? ' is-sel' : '';
        html += '<div class="rn-mf-sheet-opt' + sel + '" data-sheet-type="' + escapeHTML(t.val) + '">' +
          '<div class="rn-mf-sheet-ck"></div>' +
          '<span class="rn-mf-sheet-opt__name" style="color:' + COLOR_BY_CLASS[t['class']] + '">' + escapeHTML(t.label) + '</span>' +
          (t.sub ? '<span class="rn-mf-sheet-opt__sub">' + escapeHTML(t.sub) + '</span>' : '') +
          '</div>';
      });
    });

    /* Region */
    html += '<div class="rn-mf-sheet-group-label">Region</div>';
    var regions = [
      ['all','All Regions'],['northeast','Northeast'],['southeast','Southeast'],
      ['midwest','Midwest'],['southwest','Southwest'],['west','West'],
      ['mexico','Mexico'],['canada','Canada']
    ];
    regions.forEach(function(r) {
      var sel = state.region === r[0] ? ' is-sel' : '';
      html += '<div class="rn-mf-sheet-opt' + sel + '" data-sheet-region="' + r[0] + '">' +
        '<div class="rn-mf-sheet-ck"></div>' +
        '<span class="rn-mf-sheet-opt__name" style="color:#fff">' + escapeHTML(r[1]) + '</span></div>';
    });

    /* Toggles */
    html += '<div class="rn-mf-sheet-group-label">Filters</div>';
    html += '<div class="rn-mf-sheet-opt rn-mf-sheet-opt--toggle' + (state.showClubs ? ' is-sel' : '') + '" data-sheet-clubs>' +
      '<div class="rn-mf-sheet-ck"></div>' +
      '<span class="rn-mf-sheet-opt__name" style="color:#fff">Clubs Only</span></div>';
    html += '<div class="rn-mf-sheet-opt rn-mf-sheet-opt--toggle' + (state.vendorsOn ? ' is-sel' : '') + '" data-sheet-vendors>' +
      '<div class="rn-mf-sheet-ck"></div>' +
      '<span class="rn-mf-sheet-opt__name" style="color:#fff">Vendors</span></div>';

    if (state.vendorsOn) {
      var VENDOR_TYPES = [
        ['shop & install',      'Shop & Install'],
        ['performance tuning',  'Performance Tuning'],
        ['suspension',          'Suspension'],
        ['lighting',            'Lighting'],
        ['parts & accessories', 'Parts & Accessories'],
        ['wheels & tires',      'Wheels & Tires'],
        ['apparel & gear',      'Apparel & Gear'],
        ['overlanding',         'Overlanding']
      ];
      VENDOR_TYPES.forEach(function(vt) {
        var sel = state.vendorTypes.indexOf(vt[0]) !== -1;
        html += '<div class="rn-mf-sheet-opt rn-mf-sheet-opt--sub' + (sel ? ' is-sel' : '') + '" data-sheet-vtype="' + escapeHTML(vt[0]) + '">' +
          '<div class="rn-mf-sheet-ck"></div>' +
          '<span class="rn-mf-sheet-opt__name" style="color:#fff">' + escapeHTML(vt[1]) + '</span></div>';
      });
    }

    /* Date */
    html += '<div class="rn-mf-sheet-group-label">Date</div>';
    html += '<div class="rn-mf-sheet-date-row">' +
      '<select class="rn-mf-sheet-date-select" data-sheet-month aria-label="Month"><option value="">Any month</option>';
    for (var i = 1; i <= 12; i++) {
      html += '<option value="' + i + '"' + (state.month === i ? ' selected' : '') + '>' + MONTH_NAMES[i] + '</option>';
    }
    html += '</select><select class="rn-mf-sheet-date-select" data-sheet-year aria-label="Year">' +
      '<option value="">Year</option>' +
      '<option value="2026"' + (state.year === 2026 ? ' selected' : '') + '>2026</option>' +
      '<option value="2027"' + (state.year === 2027 ? ' selected' : '') + '>2027</option>' +
      '<option value="2028"' + (state.year === 2028 ? ' selected' : '') + '>2028</option>' +
      '</select></div>';
    html += '<div class="rn-mf-sheet-opt rn-mf-sheet-opt--toggle' + (state.hidePast ? ' is-sel' : '') + '" data-sheet-hidepast>' +
      '<div class="rn-mf-sheet-ck"></div>' +
      '<span class="rn-mf-sheet-opt__name" style="color:#fff">Upcoming only</span></div>';

    /* Get Involved — Google Form links passed in via initConfig.
       Mirrors the desktop CTA dropdown so the forms stay reachable
       on mobile, where the desktop bar controls are hidden. */
    var ctas = [];
    if (config.interestFormUrl) {
      ctas.push({ url: config.interestFormUrl, title: config.interestFormLabel || 'Request a Run in Your Area', desc: config.interestFormDesc || '' });
    }
    if (config.leadFormUrl) {
      ctas.push({ url: config.leadFormUrl, title: config.leadFormLabel || 'Lead a Run in Your Area', desc: config.leadFormDesc || '' });
    }
    if (ctas.length) {
      html += '<div class="rn-mf-sheet-group-label">' + escapeHTML(config.ctaLabel || 'Build Your Local Pack') + '</div>';
      ctas.forEach(function(cta) {
        html += '<a class="rn-mf-sheet-cta" href="' + escapeHTML(cta.url) + '" target="_blank" rel="noopener">' +
          '<div class="rn-mf-sheet-cta__body">' +
          '<span class="rn-mf-sheet-cta__title">' + escapeHTML(cta.title) + '</span>' +
          (cta.desc ? '<span class="rn-mf-sheet-cta__desc">' + escapeHTML(cta.desc) + '</span>' : '') +
          '</div>' +
          '<svg class="rn-mf-sheet-cta__arrow" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg>' +
          '</a>';
      });
    }

    return html;
  }

  /* Floating filter button visibility — hidden until the user has
     scrolled the map area into view, hidden again once past it.
     Uses the existing .is-hidden class (opacity + translateY). */
  function bindButtonVisibility(btn) {
    if (btnObserver) { btnObserver.disconnect(); btnObserver = null; }
    if (!('IntersectionObserver' in window)) return; /* stays visible */

    var targets = [];
    var mapEl   = document.querySelector('.rn-raptor-map');
    var cardsEl = document.querySelector('.rn-event-cards');
    if (mapEl)   targets.push(mapEl);
    if (cardsEl) targets.push(cardsEl);
    if (!targets.length) {
      var selfEl = document.getElementById('rn-map-filters-' + config.sectionId);
      if (selfEl) targets.push(selfEl);
    }
    if (!targets.length) return;

    btn.classList.add('is-hidden');

    /* Per-target intersection state, keyed by stable index. Button
       shows while ANY observed section is in view. */
    var inView = targets.map(function() { return false; });
    btnObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(en) {
        var idx = targets.indexOf(en.target);
        if (idx > -1) inView[idx] = en.isIntersecting;
      });
      var anyVisible = inView.some(function(v) { return v; });
      btn.classList.toggle('is-hidden', !anyVisible);
    }, {
      /* Trigger once the section is meaningfully into the viewport,
         not at the first touching pixel. */
      rootMargin: '0px 0px -20% 0px'
    });
    targets.forEach(function(t) { btnObserver.observe(t); });
  }

  function bindMobileSheet() {
    var sid      = config.sectionId;
    var sheet    = document.getElementById(config.sheetId);
    var overlay  = document.getElementById(config.sheetOverlayId);
    var closeBtn = document.getElementById(config.sheetCloseId);
    var clearBtn = document.getElementById(config.sheetClearId);
    var applyBtn = document.getElementById(config.sheetApplyId);
    var body     = document.getElementById(config.sheetBodyId);

    if (!sheet || !overlay) return;

    var existingBtn = document.getElementById('rn-mf-mobile-btn-' + sid);
    if (existingBtn) existingBtn.parentNode.removeChild(existingBtn);

    var mobileBtn = document.createElement('button');
    mobileBtn.id = 'rn-mf-mobile-btn-' + sid;
    mobileBtn.className = 'rn-mf-mobile-btn';
    mobileBtn.type = 'button';
    mobileBtn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 3h12M3 7h8M5 11h4"/></svg>' +
      'Filter Events' +
      '<span class="rn-mf-mobile-badge" id="rn-mf-mobile-badge-' + sid + '"></span>';
    document.body.appendChild(mobileBtn);
    bindButtonVisibility(mobileBtn);

    mobileBtn.addEventListener('click', function() {
      if (body) body.innerHTML = buildSheetHTML();
      bindSheetInteractions(body);
      overlay.classList.add('is-open');
      sheet.classList.add('is-open');
      sheet.setAttribute('aria-hidden', 'false');
    });

    function close() {
      overlay.classList.remove('is-open');
      sheet.classList.remove('is-open');
      sheet.setAttribute('aria-hidden', 'true');
    }

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);

    if (clearBtn) {
      clearBtn.addEventListener('click', function() {
        state.types = []; state.region = 'all'; state.showClubs = false;
        state.vendorsOn = false; state.vendorTypes = [];
        state.hidePast = false; state.month = null; state.year = null;
        if (body) body.innerHTML = buildSheetHTML();
        bindSheetInteractions(body);
        syncDesktopFromState();
        dispatch();
      });
    }

    if (applyBtn) applyBtn.addEventListener('click', close);
  }

  function bindSheetInteractions(body) {
    if (!body) return;

    body.querySelectorAll('[data-sheet-type]').forEach(function(el) {
      el.addEventListener('click', function() {
        var v = el.getAttribute('data-sheet-type');
        var idx = state.types.indexOf(v);
        if (idx === -1) state.types.push(v);
        else            state.types.splice(idx, 1);
        el.classList.toggle('is-sel', idx === -1);
        syncDesktopFromState();
        dispatch();
      });
    });

    body.querySelectorAll('[data-sheet-region]').forEach(function(el) {
      el.addEventListener('click', function() {
        var v = el.getAttribute('data-sheet-region');
        state.region = v;
        body.querySelectorAll('[data-sheet-region]').forEach(function(o) {
          o.classList.toggle('is-sel', o === el);
        });
        syncDesktopFromState();
        dispatch();
      });
    });

    var clubsEl = body.querySelector('[data-sheet-clubs]');
    if (clubsEl) clubsEl.addEventListener('click', function() {
      state.showClubs = !state.showClubs;
      clubsEl.classList.toggle('is-sel', state.showClubs);
      syncDesktopFromState();
      dispatch();
    });

    var vendorsEl = body.querySelector('[data-sheet-vendors]');
    if (vendorsEl) vendorsEl.addEventListener('click', function() {
      state.vendorsOn = !state.vendorsOn;
      if (!state.vendorsOn) state.vendorTypes = [];
      syncDesktopFromState();
      dispatch();
      /* Re-render sheet so vendor sub-types show/hide */
      body.innerHTML = buildSheetHTML();
      bindMobileSheet();
    });

    body.querySelectorAll('[data-sheet-vtype]').forEach(function(el) {
      el.addEventListener('click', function() {
        var v = el.getAttribute('data-sheet-vtype');
        var idx = state.vendorTypes.indexOf(v);
        if (idx === -1) state.vendorTypes.push(v);
        else state.vendorTypes.splice(idx, 1);
        el.classList.toggle('is-sel');
        syncDesktopFromState();
        dispatch();
      });
    });

    var pastEl = body.querySelector('[data-sheet-hidepast]');
    if (pastEl) pastEl.addEventListener('click', function() {
      state.hidePast = !state.hidePast;
      pastEl.classList.toggle('is-sel', state.hidePast);
      syncDesktopFromState();
      dispatch();
    });

    var monthSel = body.querySelector('[data-sheet-month]');
    var yearSel  = body.querySelector('[data-sheet-year]');
    if (monthSel) monthSel.addEventListener('change', function() {
      state.month = monthSel.value ? parseInt(monthSel.value, 10) : null;
      syncDesktopFromState();
      dispatch();
    });
    if (yearSel) yearSel.addEventListener('change', function() {
      state.year = yearSel.value ? parseInt(yearSel.value, 10) : null;
      syncDesktopFromState();
      dispatch();
    });
  }

  /* Sync desktop UI to current state (when mobile sheet changes things) */
  function syncDesktopFromState() {
    var sid = config.sectionId;

    /* Type chips */
    var typeWrap = document.getElementById('rn-mf-dd-type-' + sid);
    if (typeWrap) {
      typeWrap.querySelectorAll('.rn-mf-opt').forEach(function(o) {
        var v = o.getAttribute('data-val');
        var sel = state.types.indexOf(v) > -1;
        o.classList.toggle('is-sel', sel);
        o.setAttribute('aria-selected', sel ? 'true' : 'false');
      });
      updateTypeTrigger();
    }

    /* Region */
    var regWrap = document.getElementById('rn-mf-dd-region-' + sid);
    if (regWrap) {
      regWrap.querySelectorAll('.rn-mf-region-opt').forEach(function(o) {
        var sel = o.getAttribute('data-val') === state.region;
        o.classList.toggle('is-selected', sel);
        o.setAttribute('aria-selected', sel ? 'true' : 'false');
      });
      updateRegionTrigger();
    }

    /* Clubs */
    var clubs = document.querySelector('#rn-map-filters-' + sid + ' [data-toggle="clubs"]');
    if (clubs) {
      clubs.classList.toggle('is-on', state.showClubs);
      clubs.setAttribute('aria-pressed', String(state.showClubs));
    }

    /* Vendors */
    var vBtn = document.querySelector('#rn-map-filters-' + sid + ' [data-toggle="vendors"]');
    var vRev = document.getElementById(config.vendorRevealId);
    if (vBtn) {
      vBtn.classList.toggle('is-on', state.vendorsOn);
      vBtn.setAttribute('aria-pressed', String(state.vendorsOn));
    }
    if (vRev) {
      vRev.classList.toggle('is-open', state.vendorsOn);
      vRev.setAttribute('aria-hidden', String(!state.vendorsOn));
    }

    /* Date */
    var pt = document.getElementById(config.pastTrackId);
    var ms = document.getElementById(config.monthSelId);
    var ys = document.getElementById(config.yearSelId);
    if (pt) { pt.classList.toggle('is-on', state.hidePast); pt.setAttribute('aria-checked', String(state.hidePast)); }
    if (ms) ms.value = state.month !== null ? String(state.month) : '';
    if (ys) ys.value = state.year  !== null ? String(state.year)  : '';
    updateDateTrigger();
  }

  function updateMobileBadge() {
    var sid = config.sectionId;
    var badge = document.getElementById('rn-mf-mobile-badge-' + sid);
    if (!badge) return;
    var n =
      (state.types.length > 0 ? 1 : 0) +
      (state.region !== 'all' ? 1 : 0) +
      (state.showClubs ? 1 : 0) +
      (state.vendorsOn ? 1 : 0) +
      ((state.month !== null && state.year !== null) ? 1 : 0);
    badge.textContent = n;
    badge.classList.toggle('is-on', n > 0);
  }

  /* ══════════════════════════════════════════
     DISPATCH — broadcast state to map + cards
     ══════════════════════════════════════════ */
  function dispatch() {
    updateActiveStrip();
    updateMobileBadge();
    var typeForLegacy = state.types.length === 1 ? state.types[0] : 'all';
    window.dispatchEvent(new CustomEvent('rn:map-filter', {
      detail: {
        eventsOn:    state.eventsOn,
        types:       state.types.slice(),
        filter:      typeForLegacy,
        region:      state.region,
        showClubs:   state.showClubs,
        vendorsOn:   state.vendorsOn,
        vendorTypes: state.vendorTypes.slice(),
        hidePast:    state.hidePast,
        month:       state.month,
        year:        state.year
      }
    }));
  }

  /* ══════════════════════════════════════════
     PUBLIC API
     ══════════════════════════════════════════ */
  function setEventCount(n) {
    var el = document.getElementById(config.eventCountId);
    if (el) el.textContent = n;
  }

  function hideVendorsFilter() {
    var wrap = document.querySelector('#rn-map-filters-' + config.sectionId + ' .rn-mf-vendor-wrap');
    if (wrap) {
      wrap.style.display = 'none';
      var prev = wrap.previousElementSibling;
      if (prev && prev.classList.contains('rn-mf-vdiv')) prev.style.display = 'none';
    }
  }

  return {
    init: init,
    setEventCount: setEventCount,
    hideVendorsFilter: hideVendorsFilter
  };
})();

/* Listen for map's count broadcasts so the filter bar count stays in sync */
window.addEventListener('rn:map-count', function(e) {
  if (e.detail && typeof e.detail.count === 'number') {
    RNMapFilters.setEventCount(e.detail.count);
  }
});

/* Listen for map's vendor-data signal to hide vendors filter when needed */
window.addEventListener('rn:no-vendor-data', function() {
  RNMapFilters.hideVendorsFilter();
});